import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowLeft, Building2, Compass, Eye, GraduationCap, Handshake, Newspaper,
  ShieldCheck, MessagesSquare, BriefcaseBusiness, UsersRound, type LucideIcon,
} from 'lucide-react';
import { ProtectedImage } from '@/components/media';
import { LogoMark } from '@/components/Brand';
import { PublicHeader, PublicTabBar } from '@/components/PublicHeader';
import { Modal } from '@/components/ui';
import type { Course, DirectoryRow, NewsRow, PublicConsultant, PublicPartner } from '@/lib/types';
import { api } from '@/lib/api';
import { fmtNumber } from '@/lib/format';
import { useT } from '@/i18n/context';
import { TextRun } from '@/i18n/TextRun';
import { publicNewsQuery } from '@/lib/public-news';
import { PoweredBy } from '@/components/PoweredBy';
import { PublicNewsCard } from './NewsPublicPage';
import { useAuth } from '@/auth/AuthProvider';
import { Tour } from '@/components/Tour';
import { LANDING_TOUR, landingTourSeen, markLandingTourSeen, onLandingTourRequest } from '@/help/landing-tour';

/**
 * حسابات رفرفة على شبكات التواصل.
 *
 * فارغةٌ عمداً: لا حسابَ موثَّقاً في هذا المستودع، ورابطٌ مخترَع إلى حسابٍ لا
 * يملكه صاحب المنصة أسوأُ من غيابه. أضف سطراً هنا فيظهر الصفّ وحده — ولا شيء
 * آخر يتغيّر. (مسجَّلٌ في `docs/pending-decisions.md`.)
 */
const SOCIAL: ReadonlyArray<{ name: string; href: string }> = [];

/**
 * الصفحة الرئيسية العامة — أعيد تصميمها في 16 سبتمبر 2026 على طلب صاحب المشروع.
 *
 * الترتيب كما طلبه: البنر ← تعريفٌ موجز ← الشركاء ← آخر الأخبار ← التواصل.
 * والفكرة الحاكمة أن يجد الزائر **أبوابه الثلاثة في البنر نفسِه** لا تحته:
 * المراكز، والأكاديمية، والاستشارات — كلٌّ منها بعدده الحيّ من
 * مسارٍ علنيٍّ قائم، لا برقمٍ مكتوبٍ بيدٍ يشيخ.
 *
 * ومراجعة 16 سبتمبر الثانية (طلب صاحب المشروع) غيّرت أربعة أشياء في التنظيم:
 *   • **حُذف بابُ «الأخصائيون والاستشاريون»** ومعه دليلُ الأخصائيين. من يريد
 *     مختصاً يريده ليحجز عنده، لا ليقرأ اسمه في قائمة — فبابُ «الاستشارات
 *     النفسية» صار يؤدّي إلى لائحةِ الاستشاريين بأسعارهم وسيرهم وزرِّ حجزٍ لكلٍّ.
 *   • **لا رابطَ إلى مِرساة داخل صفحةٍ أخرى.** كانت المراكز وفرص العمل تُحال
 *     إلى `/directory#...`: صفحةٌ واحدة تحمل ثلاثة أقسام، فيهبط الزائر وسطها
 *     ولا يعرف أين هو. لكلٍّ صفحتُه الآن: `/centers` و`/jobs` و`/news`.
 *   • **زرّ الترويسة «تسجيل دخول»** إلى `/login` — صفحةُ دخولٍ واحدة عادية (بريدٌ
 *     وكلمة مرور) لكل الأدوار؛ واختيارُ النوع (وليّ أمر · مهني · مركز أو مدرسة)
 *     عند **إنشاء الحساب** وحده في شريطٍ فوق النموذج (18 سبتمبر 2026). والمستفيد
 *     له بابُه `/client-login` برقم جواله.
 *   • **«انضمّ إلى رفرفة» صارت «ضمّ مركزك لرفرفة»** وتؤدّي إلى نموذج المركز
 *     نفسِه لا إلى مِرساةٍ في آخر الصفحة تقول «راسلنا».
 *
 * وما حُذف سابقاً يبقى محذوفاً: «لمن» و«كيف تعمل» و«الأدلة العامة» و«من يرى ماذا»
 * و«حاسبة التكاليف». وأُسقطت معها شبكةُ «القواعد» لأن شريط الوعود يقول ما تقوله
 * بستّ كلماتٍ بدل ستّ بطاقات — والصفحة طُلب أن تكون «غير متعبة».
 */
/**
 * بطاقةُ جمهور — تُظهر أوّلَ بندين ثم تُفتح بزرٍّ بسهمٍ إلى أسفل (19 سبتمبر 2026).
 *
 * البطاقاتُ الأربع مفتوحةً تشغل ٤٨٩٦px على الهاتف، فيمرّ الزائرُ ثلاثَ شاشاتٍ
 * قبل أن يصل إلى ما بعدها — وأكثرُها لا يخصّه: وليُّ الأمر لا يقرأ إحدى عشرة
 * نقطةً عن تشغيل المدارس. فالبطاقةُ **عنوانٌ وزرّ** (`preview = 0`)، والأربعةُ
 * تُقرأ في نظرةٍ واحدة فيفتح كلٌّ بابَه.
 *
 * وفُضّل الفتحُ **في مكانه** على نافذةٍ منبثقة لسببين: النصُّ يُقرأ ولا يُعمل به
 * (والنافذةُ طقسٌ لفعلٍ لا لقراءة)، وفي الصفحة نافذةٌ واحدة أصلاً («لماذا رفرفة؟»)
 * فنمطان متنافسان يشوّشان. والثلاثةُ تبقى مرئيةً جنبَ بعضها فيفتح كلٌّ بابَه.
 *
 * والترقيمُ يستمرّ عبر القائمتين لأن العدّاد على البطاقة لا على `<ol>`: قائمتان
 * بعدّادين تبدآن من ١ مرّتين.
 */
/**
 * بطاقةُ جمهور: مربّعٌ بحجم أخواته، فيه أيقونةٌ وعنوانٌ وزرّ — والتفاصيلُ في نافذةٍ
 * تنبثق (طلب صاحب المنصة 21 سبتمبر 2026). المربّعاتُ الأربعة تُقرأ بنظرةٍ واحدة
 * جنبَ بعضها، ومن يعنيه بابٌ يفتحه وحده. ولا عددَ بجانب الزرّ: «(11)» كان يُقرأ
 * رقماً بلا معنى لمن لم يفتح بعد.
 */
function AudienceCard({ icon: Icon, title, ordered = false, children }: {
  icon: LucideIcon; title: string; ordered?: boolean; children: ReactNode;
}) {
  const t = useT();
  const [open, setOpen] = useState(false);
  const Wrap = ordered ? 'ol' : 'div';
  // النافذةُ **شقيقةُ** البطاقة لا ابنتُها: عنصرٌ بـ`position: fixed` داخل أصلٍ له
  // `transform` أو `aspect-ratio` في سياق flex يُحسب مكانُه من ذلك الأصل لا من الشاشة
  // — فظهرت أولُ نسخةٍ محشورةً في عرض البطاقة (قيس بلقطة، 21 سبتمبر 2026).
  return (
    <>
      <article className="ld-aud__card">
        <span className="ld-aud__icon" aria-hidden><Icon size={24} /></span>
        <h3>{title}</h3>
        <button type="button" className="ld-aud__more" aria-haspopup="dialog" onClick={() => setOpen(true)}>
          <span className="ld-aud__label">{t('landing.aud.open')}</span> <ArrowLeft size={16} aria-hidden className="ld-aud__arrow" />
        </button>
      </article>
      {open && (
        <Modal title={title} onClose={() => setOpen(false)} wide>
          <Wrap className="ld-aud__body">{children}</Wrap>
        </Modal>
      )}
    </>
  );
}

export function LandingPage() {
  const signedIn = useAuth().status === 'authenticated';
  const t = useT();
  // جولةُ الصفحة (18 أكتوبر 2026): وحدها في أول زيارةٍ لزائرٍ لم يدخل، وبزرّ «جولة» متى شاء
  const [touring, setTouring] = useState(false);
  useEffect(() => onLandingTourRequest(() => setTouring(true)), []);
  useEffect(() => {
    if (signedIn || landingTourSeen()) return;
    const id = window.setTimeout(() => setTouring(true), 900);
    return () => window.clearTimeout(id);
  }, [signedIn]);
  const news = useQuery(publicNewsQuery());
  // شركاءُ النجاح (0102): شعاراتٌ بالترتيب الذي اختارته إدارةُ المنصة — لا قسمَ إن لم يوجد شريك
  const partners = useQuery({ queryKey: ['public', 'partners'], queryFn: () => api.get<PublicPartner[]>('/public/partners', { institutionId: null, anonymous: true }), staleTime: 5 * 60_000 });
  const partnerRows = partners.data ?? [];
  const centers = useQuery({
    queryKey: ['public', 'directory'],
    queryFn: () => api.get<DirectoryRow[]>('/public/directory', { institutionId: null, anonymous: true }),
  });
  const consultants = useQuery({
    queryKey: ['public', 'consultants', 'all'],
    queryFn: () => api.get<PublicConsultant[]>('/public/consultants', { institutionId: null, anonymous: true }),
  });
  const courses = useQuery({
    queryKey: ['public', 'courses', '', ''],
    queryFn: () => api.get<Course[]>('/public/courses', { institutionId: null, anonymous: true }),
  });

  const latestNews: NewsRow[] = (news.data ?? []).slice(0, 3);
  const centerRows = centers.data ?? [];
  /** العدد وحده يُقرأ هنا: القسم الأكاديميّ صار صفحتَه (18 سبتمبر 2026) وبقي البابُ يعدّ */
  const courseRows = courses.data ?? [];
  const freeCount = courseRows.filter((c) => c.price === 0).length;

  return (
    <div className="landing">
      <a className="skip-link" href="#main">تخطَّ إلى المحتوى</a>
      <PublicHeader />

      <main id="main" tabIndex={-1}>
        {/* ── البنر: الشعار، ثم الأبواب الثلاثة في الشريط نفسِه ─────────── */}
        <section className="ld-hero" aria-labelledby="hero-title" data-tour="ld:hero">
          <div className="wrap">
            <div>
              <span className="kicker"><ShieldCheck size={14} /> {t('landing.hero.kicker')}</span>
              <h1 id="hero-title"><TextRun>{t('landing.hero.slogan')}</TextRun></h1>
              <p className="ld-hero__tagline"><TextRun>{t('landing.hero.tagline')}</TextRun></p>
              {/* لا فقرةَ تعريفٍ هنا: نصُّها هو «رؤيتنا» نفسُه، وقد كان يُقرأ مرّتين
                  في شاشةٍ واحدة. البنرُ اسمٌ وشعارٌ وبابان، والتعريفُ تحته (19 سبتمبر 2026). */}
              <div className="ld-hero__actions">
                {signedIn
                  ? <Link to="/home" className="btn btn--primary">حسابي</Link>
                  : <Link to="/login" className="btn btn--primary">{t('landing.cta.signin')}</Link>}
                <Link to="/register/center" className="btn btn--secondary">{t('landing.hero.join')}</Link>
              </div>
            </div>
            <div className="ld-hero__art" aria-hidden="true">
              <span className="ld-petal ld-petal--a" />
              <span className="ld-petal ld-petal--b" />
              <span className="ld-petal ld-petal--c" />
              <img src="/brand/rafrafa-mark.webp" alt="" width={900} height={1066} fetchPriority="high" decoding="async" />
              <b className="ld-hero__wordmark">{t('brand.name')}</b>
            </div>
          </div>
        </section>

        {/*
          الرؤيةُ والرسالة تحت الشعار مباشرةً — أولُ ما يُقرأ بعد الاسم. وهما نصٌّ
          طويلٌ يُقرأ لا شعارٌ يُزيَّن، فعمودان واسعان بسطرٍ مريح، لا بطاقتان ضيّقتان
          يتكسّر فيهما الكلام.
        */}
        <section className="ld-purpose" aria-label={t('landing.purpose.aria')}>
          <div className="wrap">
            <article>
              <span className="kicker"><Eye size={14} /> {t('landing.vision.t')}</span>
              <p>{t('landing.vision.d')}</p>
            </article>
            <article>
              <span className="kicker"><Compass size={14} /> {t('landing.mission.t')}</span>
              <p>{t('landing.mission.d')}</p>
            </article>
          </div>
        </section>

        {/* العنصر المميّز: ثلاثة أبواب بقمّةٍ مقوّسة كقوس الجناح، تعبر حدّ البنر */}
        <section className="ld-doors" aria-label={t('landing.doors.aria')} data-tour="ld:doors">
          <div className="wrap">
            <div className="ld-doors__grid">
              <Door
                to="/centers" icon={<Building2 size={22} aria-hidden />}
                title={t('landing.door.centers2.t')} text={t('landing.door.centers2.d')}
                meta={centerRows.length > 0
                  ? t('landing.door.centers2.meta', { n: fmtNumber(centerRows.length) })
                  : t('landing.door.centers2.metaEmpty')}
              />
              <Door
                to="/academy" icon={<GraduationCap size={22} aria-hidden />}
                title={t('landing.door.academy.t')} text={t('landing.door.academy.d')}
                meta={courseRows.length > 0
                  ? t('landing.door.academy.meta', { n: fmtNumber(courseRows.length), free: fmtNumber(freeCount) })
                  : t('landing.door.academy.metaEmpty')}
              />
              <Door
                to="/consultants" icon={<MessagesSquare size={22} aria-hidden />}
                title={t('landing.door.consult.t')} text={t('landing.door.consult.d')}
                meta={(consultants.data?.length ?? 0) > 0
                  ? t('landing.door.consult.meta2', { n: fmtNumber(consultants.data!.length) })
                  : t('landing.door.consult.meta')}
              />
            </div>
          </div>
        </section>

        {/* ── تعريفٌ موجز: فقرةٌ واحدة، أربعة وعود، ثم وحدتان ───────────── */}
        <section className="ld-about" aria-labelledby="about-title" data-tour="ld:about">
          <div className="wrap">
            {/* لا سطرَ «تعريف موجز» فوق العنوان (19 سبتمبر 2026): عنوانٌ يسأل
                «من نحن؟» لا يحتاج من يقول إن تحته تعريفاً. */}
            {/* العنوانُ في عمودٍ والتعريفُ في عمودٍ أعرض (23 سبتمبر 2026): كان التعريفُ نصفَ
                العرض ونصفُه الآخر فراغٌ — والآن يقرأ الزائرُ السؤالَ والجوابَ في سطرٍ واحد. */}
            <div className="ld-about__head">
              <h2 id="about-title">{t('landing.about.title')}</h2>
              {/* «من نحن؟» تقول ما نحن — لا تعيد صياغة «رؤيتنا» كما كانت تفعل قبل
                  19 سبتمبر 2026 بالكلمات نفسِها في الشاشة نفسِها. */}
              <p className="lead">{t('landing.about.lead')}</p>
            </div>

            {/*
              «لماذا رفرفة؟» عنوانٌ وجوابُه تحته مباشرةً (22 أكتوبر 2026 — ملاحظةُ مركز غايتنا: «الكبسة ما تكون فعّالة،
              الأجوبة تحت أو يكون الجواب بسيط»): كان زرّاً يفتح لوحاً بأربع ضمانات؛ صار سطراً يجيب، وبطاقاتُ الفئات تحته تفصّل.
            */}
            <div className="ld-why">
              <h2 className="ld-why__title">{t('landing.why.title')}</h2>
              <p className="ld-why__lead">{t('landing.why.lead')}</p>
              <p className="ld-why__more">{t('landing.why.more')}</p>
            </div>

            {/*
              «لماذا رفرفة» — لكلِّ طرفٍ بطاقتُه (19 سبتمبر 2026، بنصّ صاحب المنصة).
              كانت أربعَ بطاقاتٍ تتكلّم عن ضماناتِ المنصة (سجلٌّ لا يُمحى، موافقات،
              عزل، جهةٌ مشرفة) — وهي أجوبةُ سؤالٍ لم يسأله الزائر بعد. والزائرُ
              يسأل أولاً: «وأنا، ما الذي يعنيني منها؟» فصارت ثلاثَ بطاقاتٍ بعدد
              من يدخلون: مؤسسةٌ، وأسرة، ومهنيّ. والضماناتُ باقيةٌ خلف «لماذا رفرفة؟».
            */}
            <div className="ld-aud" aria-label={t('landing.aud.aria')}>
              <AudienceCard icon={Building2} title={t('landing.aud.inst.t')} ordered>
                {([1, 2, 3, 4, 5, 6] as const).map((i) => (
                  <li key={i}><b>{t(`landing.aud.inst.${i}.t`)}</b><span>{t(`landing.aud.inst.${i}.d`)}</span></li>
                ))}
              </AudienceCard>

              <AudienceCard icon={GraduationCap} title={t('landing.aud.school.t')} ordered>
                {([1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11] as const).map((i) => (
                  <li key={i}><b>{t(`landing.aud.school.${i}.t`)}</b><span>{t(`landing.aud.school.${i}.d`)}</span></li>
                ))}
              </AudienceCard>

              {/* مرقّمةٌ كالبطاقات الثلاث الأخرى (طلب صاحب المنصة 20 سبتمبر 2026): العنوانُ
                  الجامع بندُها الأول، فلا فقرةٌ سائبة فوق قائمةٍ مرقّمة */}
              <AudienceCard icon={UsersRound} title={t('landing.aud.parent.t')} ordered>
                <li><b>{t('landing.aud.parent.lead')}</b><span>{t('landing.aud.parent.1')}</span></li>
                {([2, 3, 4] as const).map((i) => <li key={i}><span>{t(`landing.aud.parent.${i}`)}</span></li>)}
              </AudienceCard>

              <AudienceCard icon={BriefcaseBusiness} title={t('landing.aud.pro.t')} ordered>
                {([1, 2, 3, 4, 5, 6, 7] as const).map((i) => (
                  <li key={i}><b>{t(`landing.aud.pro.${i}.t`)}</b><span>{t(`landing.aud.pro.${i}.d`)}</span></li>
                ))}
              </AudienceCard>
            </div>

            {/*
              كانت هنا بطاقتا «المدرسة العادية» و«مركز التربية الخاصة» — وقد صارتا
              تكراراً بعد بطاقات الجمهور فوقهما مباشرةً («لماذا رفرفة للمراكز؟»
              و«… للمدارس؟») بنصٍّ أوفى. وحلّ محلَّهما ما كانت الصفحةُ تفتقده:
              **طلبٌ صريح** — الزائرُ قرأ ما يعنيه ثم لم يجد بعده باباً، وأقربُ زرِّ
              تسجيلٍ في أعلى الصفحة قبل أن يقرأ شيئاً (19 سبتمبر 2026).
            */}
            <div className="ld-start">
              <h3>{t('landing.start.title')}</h3>
              <p>{t('landing.start.lead')}</p>
              <div className="ld-start__row">
                <Link to="/register/guardian" className="btn btn--primary">{t('landing.join.guardian')}</Link>
                <Link to="/register/professional" className="btn btn--secondary">{t('landing.cta.professional')}</Link>
                {/* «ضُمّ مركزك» خرج من هنا (21 سبتمبر 2026) — بابُ المراكز في قسمها وفي الترويسة */}
              </div>
            </div>
          </div>
        </section>

        {/* ── شركاء النجاح — شعاراتٌ بالترتيب (0102) ──────────────────── */}
        {partnerRows.length > 0 && (
          <section className="ld-partners" aria-labelledby="partners-title">
            <div className="wrap">
              <div className="ld-sec-head">
                <div>
                  <span className="kicker"><LogoMark size={14} /> {t('landing.partners.kicker')}</span>
                  <h2 id="partners-title">{t('landing.partners.title')}</h2>
                  <p className="lead" style={{ margin: 0 }}>{t('landing.partners.lede')}</p>
                </div>
              </div>
              {/*
                شريطٌ متحرّك (20 أكتوبر 2026 — «تخليها تتحرك كشريط وراها لون منير شفاف»): القائمةُ مكرّرةٌ مرّتين
                لحلقةٍ بلا قطع (الثانيةُ مخفيّةٌ عن القارئ الآلي)، ويتوقّف عند المؤشّر أو التركيز.
                ومن طلب تقليلَ الحركة في جهازه يرى شبكةً ثابتة: سبعةُ أعمدةٍ على الكمبيوتر وأربعةٌ على الجوال.
                26 أكتوبر 2026 — طلبُ صاحب المنصة: «رجّع شركاء النجاح زي ما كان قبل لكن بدون اللمعة أو الشعار اللي يطير»:
                عاد الشريطُ ببطاقاته وبتلتَي الخلفية، وزالت اللمعةُ العابرة وفراشةُ رفرفة الطائرة (ومدينةُ 25 أكتوبر كلُّها).
              */}
              <div className="ld-partners__strip">
                <span className="ld-petal ld-partners__petal ld-partners__petal--a" aria-hidden="true" />
                <span className="ld-petal ld-partners__petal ld-partners__petal--b" aria-hidden="true" />
                <div className="ld-partners__viewport">
                  <div className="ld-partners__track">
                    <PartnerList rows={partnerRows} />
                    <PartnerList rows={partnerRows} ariaHidden />
                  </div>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* ── آخر الأخبار — لا قسم إن لم يُنشر شيء بعد ─────────────────── */}
        {latestNews.length > 0 && (
          <section className="ld-news" aria-labelledby="news-title">
            <div className="wrap">
              <div className="ld-sec-head">
                <div>
                  <span className="kicker"><Newspaper size={14} /> {t('landing.news.kicker')}</span>
                  <h2 id="news-title">{t('landing.news.title')}</h2>
                </div>
                <Link to="/news" className="btn btn--secondary">{t('landing.news.all')}</Link>
              </div>
              <div className="ld-news__grid">
                {latestNews.map((n) => <PublicNewsCard key={n.id} row={n} compact />)}
              </div>
            </div>
          </section>
        )}

        {/* ── التواصل والسوشيال ميديا — آخر الصفحة كما طُلب ────────────── */}
        {/*
          عمودُ الروابط لا يُصيَّر أصلاً حين لا حساباتِ تواصلٍ موثَّقة (`SOCIAL` فارغة
          عمداً): كان الشبكةُ تحجز له نصفَ العرض فيبقى فراغٌ كبيرٌ بلا سبب، والقسمُ
          كلُّه سطرٌ ونصف في مساحةِ شاشة (19 سبتمبر 2026).
        */}
        <section className={`ld-contact${SOCIAL.length === 0 ? ' ld-contact--bare' : ''}`} id="contact" aria-labelledby="contact-title" data-tour="ld:contact">
          <div className="wrap">
            <div className="ld-contact__text">
              <span className="kicker"><UsersRound size={14} /> {t('landing.contact.kicker')}</span>
              <h2 id="contact-title">{t('landing.contact.title')}</h2>
              <p className="lead">{t('landing.contact.lead')}</p>
              {/* لا سطرَ «نعمل في المملكة… والجهة المشرفة الإلزامية…» (19 سبتمبر 2026):
                  بيانُ نطاقٍ ورقابةٍ في صفحةِ تعريفٍ يقرؤه المركزُ قبل أن يعرفنا. */}
            </div>
            {SOCIAL.length > 0 && (
              <div className="ld-contact__links">
                <ul className="ld-social" aria-label={t('landing.contact.socialAria')}>
                  {SOCIAL.map((s) => (
                    <li key={s.name}>
                      <a href={s.href} target="_blank" rel="noreferrer noopener">{s.name}</a>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </section>
      </main>

      {touring && <Tour steps={LANDING_TOUR} onDone={() => { setTouring(false); markLandingTourSeen(); }} />}
      <footer className="ld-footer">
        <div className="wrap">
          <div>
            <img src="/brand/rafrafa-mark.webp" alt={t('landing.footer.logoAlt')} width={37} height={44} loading="lazy" decoding="async" />
            <p>{t('landing.footer.about')}</p>
          </div>
          <div>
            <h4>{t('landing.footer.signInCol')}</h4>
            <ul>
              <li><Link to="/login">{t('landing.cta.signin')}</Link></li>
              <li><Link to="/register/professional">{t('landing.cta.professional')}</Link></li>
              <li><Link to="/register/guardian">{t('landing.join.guardian')}</Link></li>
              <li><Link to="/register/center">{t('landing.join.center')}</Link></li>
              <li><Link to="/guardians/activate">{t('landing.footer.guardianActivate')}</Link></li>
            </ul>
          </div>
          <div>
            <h4>رفرفة</h4>
            <ul>
              <li><Link to="/contact">تواصل معنا</Link></li>
              <li><Link to="/contact?kind=DEMO">اطلب عرضاً تجريبياً لمؤسستك</Link></li>
              <li><Link to="/privacy">سياسة الخصوصية</Link></li>
              <li><Link to="/terms">شروط الاستخدام</Link></li>
            </ul>
          </div>
          {/*
            التذييل لا يكرّر الترويسة (18 سبتمبر 2026): قائمةُ الأقسام العلنية كانت
            نسخةً ثانية من شريط التنقّل فوقها حرفاً بحرف. والتذييل يحمل ما لا مكان
            له فوق — أبوابَ الدخول والتسجيل — لا صدىً لما قرأه الزائر قبل قليل.
            ومكانُ الشركاء (وزارات وسفارات وجمعيات ومنظمات) قسمٌ في الصفحة حين
            تصل قائمتُهم، لا سطورٌ في التذييل (القرار A-84).
          */}
          <div className="legal">
            <span>{t('public.footer.rights', { year: new Date().getFullYear() })}</span>
            <span>{t('landing.footer.privacy')}</span>
            <PoweredBy />
          </div>
        </div>
      </footer>
      <PublicTabBar />

    </div>
  );
}

/** بطاقاتُ شركاء النجاح — قائمةٌ واحدة تُصيَّر مرّتين في الشريط المتحرّك */
function PartnerList({ rows, ariaHidden }: { rows: PublicPartner[]; ariaHidden?: boolean }) {
  return (
    <ul className="ld-partners__list" aria-hidden={ariaHidden || undefined}>
      {rows.map((pr) => {
        const body = (
          <>
            <span className={`ld-partner__logo${pr.logoShape === 'SQUARE' ? ' ld-partner__logo--square' : ''}`}>
              {pr.logoFileId
                ? <ProtectedImage path={`/public/files/${pr.logoFileId}`} alt="" size={64} anonymous />
                : <Handshake size={22} aria-hidden />}
            </span>
            <b className="ld-partner__name">{pr.nameAr}</b>
            <span className="ld-partner__en" dir="ltr">{pr.nameEn}</span>
          </>
        );
        return (
          <li key={pr.id}>
            {pr.websiteUrl
              ? <a className="ld-partner" href={pr.websiteUrl} target="_blank" rel="noopener noreferrer" tabIndex={ariaHidden ? -1 : undefined}>{body}</a>
              : <div className="ld-partner">{body}</div>}
          </li>
        );
      })}
    </ul>
  );
}

/** بابٌ من الأبواب الأربعة — رأسٌ مقوّس كقوس الجناح، وعمودٌ بلون الهوية على حرفه */
function Door({ to, icon, title, text, meta }: {
  to: string; icon: React.ReactNode; title: string; text: string; meta: string;
}) {
  return (
    <Link to={to} className="ld-door">
      <span className="ld-door__icon">{icon}</span>
      <b className="ld-door__title">{title}</b>
      <span className="ld-door__text">{text}</span>
      <span className="ld-door__meta">{meta}</span>
    </Link>
  );
}
